import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { ReactElement, ReactNode } from 'react';
import { FormattedMessage, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';

import { SearchInput } from 'components/common/SearchInput';
import {
  ButtonContained,
  ButtonIcon,
  ButtonText,
} from 'components/general';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { DocumentationDirectoryList } from './DocumentationDirectoryList';
import {
  actionsBoxSx,
  actionsLeftSx,
  dialogContentSx,
  dialogPaperSx,
  dialogTitleSx,
  listWrapperSx,
  searchBoxSx,
} from './styles';

export interface DocumentationBrowserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  titleFallbackId: string;
  searchDataQa: string;
  browser: {
    currentDirectoryName: string | null;
    directoryStack: string[];
    directories: DirectoryModel[];
    emptyDirectoryIds?: Set<string>;
    documentations: DocumentationModel[];
    loading: boolean;
    loadError: string | null;
    searchPhrase: string;
    setSearchPhrase: (value: string) => void;
    selectedDirectoryId: string | null;
    selectedDocumentationLocalIds?: string[];
    goUp: () => void;
    handleRowClick: (id: string) => void;
    handleDoubleClick: (id: string) => void;
    handleDocumentationRowClick?: (id: string) => void;
  };
  leftActions?: ReactNode;
  /** Choices of the action shown above its buttons (e.g. what to link to). */
  options?: ReactNode;
  confirmLabelId: string;
  onConfirm: () => void;
  confirmDisabled: boolean;
  confirmLoading?: boolean;
}

export function DocumentationBrowserDialog({
  isOpen,
  onClose,
  titleFallbackId,
  searchDataQa,
  browser,
  leftActions,
  options,
  confirmLabelId,
  onConfirm,
  confirmDisabled,
  confirmLoading = false,
}: DocumentationBrowserDialogProps): ReactElement {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        {browser.directoryStack.length > 0 && (
          <ButtonIcon onClick={browser.goUp} size='small'>
            <ArrowBack />
          </ButtonIcon>
        )}
        {browser.currentDirectoryName ?? (
          <FormattedMessage id={unsafeTranslationKey(titleFallbackId)} />
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={dialogContentSx}>
        <Box sx={searchBoxSx}>
          <SearchInput
            value={browser.searchPhrase}
            searchChange={browser.setSearchPhrase}
            searchClear={() => browser.setSearchPhrase('')}
            placeholderTextId='dialog_placeholder_search'
            dataQa={searchDataQa}
          />
        </Box>

        <Divider />

        <Box sx={listWrapperSx}>
          <DocumentationDirectoryList
            directories={browser.directories}
            emptyDirectoryIds={browser.emptyDirectoryIds}
            documentations={browser.documentations}
            loading={browser.loading}
            loadError={browser.loadError}
            selectedDirectoryId={browser.selectedDirectoryId}
            selectedDocumentationLocalIds={
              browser.selectedDocumentationLocalIds
            }
            onDocumentationRowClick={browser.handleDocumentationRowClick}
            onRowClick={browser.handleRowClick}
            onRowDoubleClick={browser.handleDoubleClick}
          />
        </Box>

        <Divider />

        {options}

        <Box sx={actionsBoxSx}>
          <Box sx={actionsLeftSx}>
            {leftActions}

            <ButtonText onClick={onClose}>
              <FormattedMessage id={CommonTranslationKeys.cancel} />
            </ButtonText>
          </Box>

          <ButtonContained
            onClick={onConfirm}
            disabled={confirmDisabled}
            endIcon={
              confirmLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : undefined
            }
          >
            <FormattedMessage id={unsafeTranslationKey(confirmLabelId)} />
          </ButtonContained>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
